import { useEffect, useState } from 'react';
import { Clock, AlertTriangle, CheckCircle2, Calendar } from 'lucide-react';
import api from '@host/axios';

import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';
import { ymdInTimezone } from '../../i18n/displayTimezone.js';

/**
 * AttendanceLookupCard — render field type `attendance_lookup` trong form approval.
 *
 * Watch `sourceDateValue` (string YYYY-MM-DD từ form_data của date field nguồn).
 * Khi đổi → gọi GET /attendance/lookup-self?date=... → render card check-in/out
 * cũ của user. Dùng để user "Bổ sung chấm công" thấy giờ hiện tại trước khi
 * điền giờ mới.
 *
 * Props:
 *   - sourceDateValue: string|null — giá trị date field nguồn
 *   - show: string[] — danh sách show field (check_in_time, check_out_time, status, …)
 *   - placeholder: string — text khi chưa có date
 */
// Giới hạn ngày được chọn theo cấu hình field.date_limit (mirror
// @cnv/server-shared approvalSubmitWindow). Tính theo múi hiển thị của hệ (Work/Core: giờ VN +07; Site Connect:
// múi workspace — máy chủ Connect truyền cùng múi đó vào checkCorrectionWindow), độc lập TZ máy.
function vnDateStr(d) {
  return ymdInTimezone(d);
}

// Trả { min, max } (YYYY-MM-DD inclusive; null = không chặn) từ date_limit config.
function computeDateRange(dateLimit) {
  const today = vnDateStr(new Date());
  const mode = dateLimit?.mode || 'all';
  if (mode === 'last_days') {
    const n = Math.max(0, Math.floor(Number(dateLimit?.days) || 0));
    return { min: vnDateStr(new Date(Date.now() - n * 86400000)), max: today };
  }
  if (mode === 'this_month') {
    return { min: `${today.slice(0, 7)}-01`, max: today };
  }
  if (mode === 'this_and_last_month') {
    let y = Number(today.slice(0, 4));
    let m = Number(today.slice(5, 7)) - 1;
    if (m < 1) { m = 12; y -= 1; }
    return { min: `${y}-${String(m).padStart(2, '0')}-01`, max: today };
  }
  return { min: null, max: null }; // 'all'
}

function dateLimitNote(dateLimit, min) {
  const mode = dateLimit?.mode || 'all';
  if (mode === 'last_days') return i18n._(msg`Chỉ chọn được trong vòng ${Math.max(0, Math.floor(Number(dateLimit?.days) || 0))} ngày gần nhất (từ ${min}).`);
  if (mode === 'this_month') return i18n._(msg`Chỉ chọn được ngày trong tháng này.`);
  if (mode === 'this_and_last_month') return i18n._(msg`Chỉ chọn được ngày trong tháng này và tháng trước.`);
  return '';
}

function normalizeDate(v) {
  if (!v) return null;
  // Date object → ISO date
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null;
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  // ISO string với T → cắt date part
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return normalizeDate(d);
  }
  return null;
}

function StatusBadge({ status, label }) {
  if (!status) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">— {label}</span>;
  }
  const color = status === 'on_time'   ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : status === 'late'      ? 'bg-amber-50 text-amber-700 border-amber-200'
              : status === 'early_leave' ? 'bg-orange-50 text-orange-700 border-orange-200'
              : status === 'late_and_early' ? 'bg-rose-50 text-rose-700 border-rose-200'
              : status === 'absent'    ? 'bg-rose-50 text-rose-700 border-rose-200'
              : status === 'forgot_clock' ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-gray-100 text-gray-600 border-gray-200';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${color}`}>{label}</span>;
}

export default function AttendanceLookupCard({
  sourceDateValue,
  show,
  placeholder,
  onSnapshot,
  // standalone mode — card render date picker tích hợp + cross-write date về form_data
  // qua callback onPickDate. Dùng cho template attendance_fix/late_arrival/early_leave:
  // ẩn _kind_attendance_date riêng, gộp date picker vào card này để UX 1-step.
  standalone = false,
  onPickDate,
  readOnly = false,
  // dateLimit — cấu hình giới hạn ngày được chọn của field (date_limit). null = tất cả.
  dateLimit = null,
}) {
  const {
    t
  } = useLingui();

  const date = normalizeDate(sourceDateValue);
  // Giới hạn ngày được chọn theo config — chỉ áp ở standalone (card có date picker).
  const { min: minDate, max: maxDate } = standalone ? computeDateRange(dateLimit) : { min: null, max: null };
  const limitNote = standalone && minDate ? dateLimitNote(dateLimit, minDate) : '';
  const [state, setState] = useState({ loading: false, data: null, error: null });

  useEffect(() => {
    if (!date) {
      setState({ loading: false, data: null, error: null });
      onSnapshot?.(null);
      return;
    }
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    const params = { date };
    if (Array.isArray(show) && show.length > 0) params.show = show.join(',');
    api.get('/attendance/lookup-self', { params })
      .then((r) => {
        if (cancelled) return;
        const dto = r.data?.data || null;
        setState({ loading: false, data: dto, error: null });
        // Stash snapshot vào form_data — render display mode đọc lại từ đây,
        // KHÔNG fetch lại (admin xem đơn cũ thấy đúng giờ-cũ-tại-submit-time
        // kể cả khi attendance đã bị đơn này sửa sau duyệt).
        onSnapshot?.(dto);
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, data: null, error: e.response?.data?.message || t`Không tải được dữ liệu chấm công` });
      });
    return () => { cancelled = true; };
  }, [date, show?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date picker tích hợp (standalone mode) — render đầu card.
  const datePicker = standalone ? (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <Calendar size={15} className="text-gray-500 shrink-0" />
        <input
          type="date"
          value={date || ''}
          min={minDate || undefined}
          max={maxDate || undefined}
          onChange={(e) => {
            const v = e.target.value || null;
            // Chặn ngoài phạm vi (đề phòng browser cũ bỏ qua min/max).
            if (v && minDate && v < minDate) return;
            if (v && maxDate && v > maxDate) return;
            onPickDate?.(v);
          }}
          disabled={readOnly || !onPickDate}
          className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      </div>
      {limitNote && (
        <div className="text-[11px] text-gray-400 mt-1 pl-6">{limitNote}</div>
      )}
    </div>
  ) : null;

  // Empty state — chưa chọn ngày
  if (!date) {
    return (
      <div className={`border border-dashed border-gray-200 rounded-xl px-4 py-3 bg-gray-50 ${standalone ? '' : 'text-sm text-gray-400 flex items-center gap-2'}`}>
        {standalone ? (
          <>
            {datePicker}
            <div className="text-xs text-gray-400 flex items-center gap-1.5 pl-6">
              <Clock size={13} />
              <span>{placeholder || t`Chọn ngày để xem giờ chấm công hiện tại`}</span>
            </div>
          </>
        ) : (
          <>
            <Clock size={15} />
            <span>{placeholder || t`Chọn ngày để xem giờ chấm công hiện tại`}</span>
          </>
        )}
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="border border-gray-200 rounded-xl px-4 py-3 bg-gray-50">
        {datePicker}
        <div className="animate-pulse">
          <div className="h-3 bg-gray-200 rounded w-32 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-48" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="border border-rose-200 rounded-xl px-4 py-3 bg-rose-50">
        {datePicker}
        <div className="text-sm text-rose-700 flex items-center gap-2">
          <AlertTriangle size={15} />
          {state.error}
        </div>
      </div>
    );
  }

  const d = state.data;
  if (!d) return null;

  // Empty: có response nhưng has_attendance=false (user chưa chấm công ngày đó)
  if (!d.has_attendance) {
    return (
      <div className="border border-amber-200 rounded-xl px-4 py-3 bg-amber-50">
        {datePicker}
        <div className="text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-medium"><Trans>Chưa có chấm công ngày</Trans> {date}</div>
            <div className="text-xs text-amber-700 mt-0.5"><Trans>Ngày này bạn chưa check-in. Hãy điền giờ mới ở các trường phía dưới.</Trans></div>
          </div>
        </div>
      </div>
    );
  }

  // Có data → render compact card
  const showSet = new Set(Array.isArray(show) && show.length > 0 ? show : ['check_in_time', 'check_out_time', 'status']);
  return (
    <div className="border border-blue-200 rounded-xl px-4 py-3 bg-blue-50/50">
      {datePicker}
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 size={14} className="text-blue-600" />
        <span className="text-xs font-medium text-blue-800"><Trans>Chấm công hiện tại của bạn ngày</Trans> {date}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
        {showSet.has('check_in_time') && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs text-gray-500"><Trans>Vào:</Trans></span>
            <span className="font-mono font-semibold text-gray-900">{d.check_in_time_label || '—'}</span>
          </div>
        )}
        {showSet.has('check_out_time') && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs text-gray-500">Ra:</span>
            <span className="font-mono font-semibold text-gray-900">{d.check_out_time_label || '—'}</span>
          </div>
        )}
        {showSet.has('work_minutes') && d.work_minutes_label && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs text-gray-500"><Trans>Tổng:</Trans></span>
            <span className="font-mono text-gray-700">{d.work_minutes_label}</span>
          </div>
        )}
        {showSet.has('status') && (
          <StatusBadge status={d.status} label={d.status_label} />
        )}
      </div>
      {showSet.has('shift') && d.shift?.name && (
        <div className="text-xs text-gray-500 mt-1.5">
          Ca: {d.shift.name} ({d.shift.start_time}–{d.shift.end_time})
        </div>
      )}
      {showSet.has('note') && d.note && (
        <div className="text-xs text-gray-500 italic mt-1"><Trans>Ghi chú:</Trans> {d.note}</div>
      )}
    </div>
  );
}
